import { FieldErrors } from 'react-hook-form'
import { Dayjs } from 'dayjs'
import { Timestamp } from 'firebase/firestore'
import { SelectOptionType } from '../../../../components/InputFields/SelectInput'
import type { AppFormControl } from '../../../../types/form.types'

export interface PricingPersonTier {
  id?: string
  persons: number
  price: number
  isDefault: boolean
}

export interface PricingConfigurationProps {
  lists: PricingListType[]
  editingPricingId?: string
  deletingPricingId?: string
  title: string
  startDate: Dayjs
  endDate: Dayjs
  spotNames: SpotNameType[]
  weekdays: WeekdayType[]
  personTiers: PricingPersonTier[]
  extraPersonPrice: number
  maxPersons: number
  isSaving: boolean
  handleSavePricing: () => void
  handleEditPricing: (pricingList: PricingListType) => void
  handleCancelEdit: () => void
  handlePricingDelete: (pricingList: PricingListType) => void
  handleStartDateChange: (date: Dayjs) => void
  handleEndDateChange: (date: Dayjs) => void
  handleSpotNamesChange: (spotNames: SpotNameType[]) => void
  handleWeekdaysChange: (weekdays: WeekdayType[]) => void
  handlePersonTierChange: (tierId: string, field: keyof Omit<PricingPersonTier, 'id'>, value: number | boolean) => void
  handleAddPersonTier: () => void
  handleDeletePersonTier: (tierId: string) => void
  isLoading: boolean
  control: AppFormControl
  errors: FieldErrors<PricingConfigurationFormData>
  spotOptions: SelectOptionType[]
}

export type WeekdayType = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

export enum Weekday {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

/*
 * This array is used allong with the .getDay() method to get the day of the week
 *
 * date.toDate().getDay() - Returns a number from 0-6 representing the day of the week:
 * 0 = Sunday
 * 1 = Monday
 * 2 = Tuesday
 * 3 = Wednesday
 * 4 = Thursday
 * 5 = Friday
 * 6 = Saturday
 *
 * Order Matters
 *
 * Weekday[date.toDate().getDay()] - Returns the day of the week
 */
export const weekdays: WeekdayType[] = [
  Weekday.SUNDAY,
  Weekday.MONDAY,
  Weekday.TUESDAY,
  Weekday.WEDNESDAY,
  Weekday.THURSDAY,
  Weekday.FRIDAY,
  Weekday.SATURDAY,
]

export interface SpotNameType {
  id: string
  name: string
}

export type PricingListType = {
  id: string
  title: string
  startDate: Timestamp
  endDate: Timestamp
  spotNames: SpotNameType[]
  weekdays: WeekdayType[]
  personTiers: PricingPersonTier[]
  extraPersonPrice: number
  maxPersons: number
}

export type PricingConfigurationType = {
  lists: PricingListType[]
  updatedAt: Timestamp
}

export interface PricingConfigurationFormData {
  title: string
  startDate: Dayjs
  endDate: Dayjs
  spotNames: SpotNameType[]
  weekdays: WeekdayType[]
  personTiers: PricingPersonTier[]
  extraPersonPrice: number
  maxPersons: number
}
